<script setup lang="ts">
import { computed } from 'vue'
import Badge from 'primevue/badge'
import type { User } from '@/types/user'
import type { UserType } from '@/utils/roleHandler'

const props = defineProps<{
  user: User | null
  loading: boolean
}>()

defineEmits<{
  confirm: []
  close: []
}>()

// Computed properties for dynamic content
const isActivating = computed(() => {
  return props.user?.status !== 'active'
})

const actionDescription = computed(() => {
  if (isActivating.value) {
    return 'Are you sure you want to activate this user? The user will be able to log in and access the system.'
  } else {
    return 'Are you sure you want to deactivate this user? The user will not be able to log in until reactivated.'
  }
})

const modalColor = computed(() => {
  return isActivating.value ? 'bg-green-50 border-green-400 text-green-700' : 'bg-red-50 border-red-400 text-red-700'
})

// Get user type badge class
const getUserTypeBadgeClass = (userType: UserType) => {
  const classMap: Record<string, string> = {
    admin: 'bg-purple-100 text-purple-800 border border-purple-200',
    agent: 'bg-blue-100 text-blue-800 border border-blue-200',
    engineer: 'bg-green-100 text-green-800 border border-green-200',
    client: 'bg-orange-100 text-orange-800 border border-orange-200',
  }
  return classMap[userType] || 'bg-gray-100 text-gray-800 border border-gray-200'
}

// Get user type label
const getUserTypeLabel = (userType: UserType) => {
  const labelMap: Record<string, string> = {
    admin: 'Administrator',
    agent: 'Agent',
    engineer: 'Engineer',
    client: 'Client',
  }
  return labelMap[userType] || userType
}

// Get status label
const getStatusLabel = (status: string) => {
  const labelMap: Record<string, string> = {
    active: 'Active',
    inactive: 'Inactive',
    pending: 'Pending',
    suspended: 'Suspended',
  }
  return labelMap[status] || status
}

// Get status badge class
const getStatusBadgeClass = (status: string) => {
  const classMap: Record<string, string> = {
    active: 'bg-green-100 text-green-800 border border-green-200',
    inactive: 'bg-gray-100 text-gray-800 border border-gray-200',
    pending: 'bg-yellow-100 text-yellow-800 border border-yellow-200',
    suspended: 'bg-red-100 text-red-800 border border-red-200',
  }
  return classMap[status] || 'bg-gray-100 text-gray-800 border border-gray-200'
}

// Format date
const formatDate = (dateString: string): string => {
  if (!dateString) return 'Never'
  try {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return dateString
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
  } catch {
    return dateString
  }
}

// Format date with time
const formatDateTime = (dateString: string): string => {
  if (!dateString) return 'Never'
  try {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return dateString
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return dateString
  }
}
</script>

<template>
  <div class="space-y-4">
    <!-- Dynamic warning/confirmation message -->
    <div :class="['border-l-4 p-4', modalColor]">
      <div class="flex">
        <div class="flex-shrink-0">
          {{ isActivating ? '✅' : '⚠️' }}
        </div>
        <div class="ml-3">
          <p class="text-sm font-medium mb-1">
            {{ isActivating ? 'Activate User' : 'Deactivate User' }}
          </p>
          <p class="text-sm">
            {{ actionDescription }}
          </p>
        </div>
      </div>
    </div>

    <div v-if="props.user" class="bg-gray-50 p-4 rounded-lg">
      <h3 class="text-sm font-medium text-gray-700 mb-2">User Details:</h3>
      <div class="space-y-3">
        <div class="flex items-center gap-3">
          <div
            class="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-600"
          >
            {{ props.user.firstName?.charAt(0) || 'U'
            }}{{ props.user.lastName?.charAt(0) || '' }}
          </div>
          <div>
            <p class="font-medium">
              {{ props.user.firstName || 'No name' }} {{ props.user.lastName || '' }}
            </p>
            <p class="text-sm text-gray-500">{{ props.user.email }}</p>
            <p v-if="props.user.username" class="text-sm text-gray-400">
              @{{ props.user.username }}
            </p>
          </div>
        </div>

        <div class="grid grid-cols-2 gap-2 text-sm">
          <div>
            <span class="text-gray-600">User Type:</span>
            <Badge
              :value="getUserTypeLabel(props.user.userType)"
              :class="getUserTypeBadgeClass(props.user.userType)"
              class="ml-2 px-2 py-0.5 text-xs"
            />
          </div>
          <div>
            <span class="text-gray-600">Current Status:</span>
            <Badge
              :value="getStatusLabel(props.user.status)"
              :class="getStatusBadgeClass(props.user.status)"
              class="ml-2 px-2 py-0.5 text-xs"
            />
          </div>
          <div>
            <span class="text-gray-600">Created:</span>
            <span class="font-medium ml-2">{{ formatDate(props.user.createdAt) }}</span>
          </div>
          <div>
            <span class="text-gray-600">Last Login:</span>
            <span class="font-medium ml-2">{{
              formatDateTime(props.user.lastLogin || '')
            }}</span>
          </div>
        </div>

        <div v-if="props.user.notes" class="pt-2 border-t border-gray-200">
          <span class="text-gray-600">Notes:</span>
          <p class="text-sm text-gray-700 mt-1">{{ props.user.notes }}</p>
        </div>

        <!-- Status change preview -->
        <div class="pt-2 border-t border-gray-200">
          <div class="flex items-center gap-2">
            <span class="text-gray-600">New Status:</span>
            <Badge
              :value="isActivating ? 'Active' : 'Inactive'"
              :class="isActivating ? 'bg-green-100 text-green-800 border border-green-200' : 'bg-gray-100 text-gray-800 border border-gray-200'"
              class="px-2 py-0.5 text-xs"
            />
            <span class="text-xs text-gray-500 ml-2">
              (Changing from {{ getStatusLabel(props.user.status) }})
            </span>
          </div>
        </div>
      </div>
    </div>


  </div>
</template>
